import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, LogOut, Upload, X, Car as CarIcon, Inbox, Pencil, Tag, RotateCcw } from "lucide-react";
import { api, imageUrl, formatPrice, formatKms, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const empty = { brand: "", model: "", year: "", ownership: "1st Owner", transmission: "Manual", fuel_type: "Petrol", kilometers: "", price: "", description: "" };

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [form, setForm] = useState(empty);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [tab, setTab] = useState("add");

  const load = () => {
    api.get("/cars").then((r) => setCars(r.data)).catch(() => {});
    api.get("/loan-enquiry").then((r) => setEnquiries(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const doLogout = () => { logout(); navigate("/"); };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, res.data.path]);
      }
      toast.success("Image(s) uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (path) => setImages((prev) => prev.filter((p) => p !== path));

  const resetForm = () => { setForm(empty); setImages([]); setEditingId(null); };

  const startEdit = (car) => {
    setEditingId(car.id);
    setForm({
      brand: car.brand, model: car.model, year: String(car.year), ownership: car.ownership,
      transmission: car.transmission, fuel_type: car.fuel_type, kilometers: String(car.kilometers),
      price: String(car.price), description: car.description || "", is_sold: car.is_sold,
    });
    setImages(car.images || []);
    setTab("add");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.brand || !form.model || !form.year || !form.price || !form.kilometers) {
      toast.error("Please fill brand, model, year, kilometers and price.");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      year: parseInt(form.year),
      kilometers: parseInt(form.kilometers),
      price: parseInt(form.price),
      images,
    };
    try {
      if (editingId) {
        await api.put(`/cars/${editingId}`, { ...payload, is_sold: form.is_sold || false });
        toast.success("Car updated!");
      } else {
        await api.post("/cars", payload);
        toast.success("Car added to stock!");
      }
      resetForm();
      setTab("stock");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save car");
    } finally {
      setSaving(false);
    }
  };

  const deleteCar = async (id) => {
    try {
      await api.delete(`/cars/${id}`);
      toast.success("Car removed from stock");
      load();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const toggleSold = async (id) => {
    try {
      const res = await api.patch(`/cars/${id}/sold`);
      toast.success(res.data.is_sold ? "Marked as SOLD" : "Marked as available");
      load();
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const inputCls = "mt-2 bg-black/40 border-white/20 focus:border-[#FF3B30] text-white";

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-24 pb-24 min-h-screen">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
        <div>
          <h1 className="font-display font-black text-3xl text-white">Admin Dashboard</h1>
          <p className="text-gray-400 text-sm mt-1">Logged in as {user?.email}</p>
        </div>
        <Button onClick={doLogout} variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10 text-white" data-testid="admin-logout"><LogOut size={16} className="mr-2" /> Logout</Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#141414] border border-white/10">
          <TabsTrigger value="add" data-testid="tab-add" className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white">{editingId ? "Edit Car" : "Add Car"}</TabsTrigger>
          <TabsTrigger value="stock" data-testid="tab-stock" className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white">Stock ({cars.length})</TabsTrigger>
          <TabsTrigger value="enquiries" data-testid="tab-enquiries" className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white">Enquiries ({enquiries.length})</TabsTrigger>
        </TabsList>

        {/* ADD / EDIT */}
        <TabsContent value="add" className="mt-8">
          <form onSubmit={submit} className="bg-[#141414] border border-white/10 rounded-2xl p-8 max-w-3xl" data-testid="add-car-form">
            {editingId && (
              <div className="flex items-center justify-between mb-6 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-lg px-4 py-3">
                <span className="text-[#FF3B30] font-semibold text-sm flex items-center gap-2"><Pencil size={15} /> Editing existing car</span>
                <button type="button" onClick={resetForm} className="text-gray-300 hover:text-white text-sm flex items-center gap-1" data-testid="cancel-edit"><X size={14} /> Cancel</button>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div><Label className="text-gray-300 text-sm">Brand *</Label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Maruti Suzuki" className={inputCls} data-testid="car-brand" /></div>
              <div><Label className="text-gray-300 text-sm">Model *</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="Swift VXI" className={inputCls} data-testid="car-model" /></div>
              <div><Label className="text-gray-300 text-sm">Year *</Label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="2019" className={inputCls} data-testid="car-year" /></div>
              <div><Label className="text-gray-300 text-sm">Price (₹) *</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="550000" className={inputCls} data-testid="car-price" /></div>
              <div><Label className="text-gray-300 text-sm">Kilometers *</Label><Input type="number" value={form.kilometers} onChange={(e) => setForm({ ...form, kilometers: e.target.value })} placeholder="45000" className={inputCls} data-testid="car-kilometers" /></div>
              <div>
                <Label className="text-gray-300 text-sm">Ownership</Label>
                <Select value={form.ownership} onValueChange={(v) => setForm({ ...form, ownership: v })}>
                  <SelectTrigger className={inputCls + " w-full"} data-testid="car-ownership"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/10 text-white">
                    {["1st Owner", "2nd Owner", "3rd Owner", "4th Owner+"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Transmission</Label>
                <Select value={form.transmission} onValueChange={(v) => setForm({ ...form, transmission: v })}>
                  <SelectTrigger className={inputCls + " w-full"} data-testid="car-transmission"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/10 text-white">
                    {["Manual", "Automatic"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Fuel Type</Label>
                <Select value={form.fuel_type} onValueChange={(v) => setForm({ ...form, fuel_type: v })}>
                  <SelectTrigger className={inputCls + " w-full"} data-testid="car-fuel"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/10 text-white">
                    {["Petrol", "Diesel", "CNG", "Electric", "Hybrid"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-5"><Label className="text-gray-300 text-sm">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Well maintained, single owner, insurance valid..." className={inputCls} data-testid="car-description" /></div>

            <div className="mt-5">
              <Label className="text-gray-300 text-sm">Photos</Label>
              <label className="mt-2 flex items-center justify-center gap-2 border border-dashed border-white/20 rounded-lg h-24 cursor-pointer hover:border-[#FF3B30]/50 transition-colors text-gray-400" data-testid="car-image-upload">
                <Upload size={18} /> {uploading ? "Uploading..." : "Click to upload car photos"}
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} disabled={uploading} />
              </label>
              {images.length > 0 && (
                <div className="flex gap-3 mt-4 flex-wrap">
                  {images.map((p) => (
                    <div key={p} className="relative w-24 h-20 rounded-md overflow-hidden border border-white/10">
                      <img src={imageUrl(p)} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeImage(p)} className="absolute top-1 right-1 bg-black/70 rounded-full p-0.5 text-white" data-testid="remove-image"><X size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button type="submit" disabled={saving} className="mt-8 bg-[#FF3B30] hover:bg-[#FF5247] text-white font-bold h-12 px-8 rounded-md" data-testid="submit-car">
              <Plus size={18} className="mr-2" /> {saving ? "Saving..." : editingId ? "Update Car" : "Add To Stock"}
            </Button>
          </form>
        </TabsContent>

        {/* STOCK */}
        <TabsContent value="stock" className="mt-8">
          {cars.length === 0 ? (
            <div className="border border-dashed border-white/15 rounded-xl py-20 text-center text-gray-400 flex flex-col items-center gap-3"><CarIcon size={32} /> No cars in stock yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cars.map((c) => (
                <div key={c.id} className="bg-[#141414] border border-white/10 rounded-lg overflow-hidden" data-testid={`stock-car-${c.id}`}>
                  <div className="aspect-video bg-black relative">
                    {c.images?.[0] ? <img src={imageUrl(c.images[0])} alt="" className={`w-full h-full object-cover ${c.is_sold ? "opacity-50" : ""}`} /> : <div className="w-full h-full flex items-center justify-center text-gray-600 text-sm">No image</div>}
                    {c.is_sold && <span className="absolute top-3 right-3 bg-[#FF3B30] text-white text-xs font-black px-3 py-1 rounded-full tracking-wider">SOLD</span>}
                  </div>
                  <div className="p-4">
                    <h3 className="font-display font-bold text-white">{c.brand} {c.model}</h3>
                    <p className="text-[#FF3B30] font-bold">{formatPrice(c.price)}</p>
                    <p className="text-gray-500 text-xs mt-1">{c.year} · {formatKms(c.kilometers)} · {c.fuel_type}</p>

                    <div className="grid grid-cols-2 gap-2 mt-4">
                      <Button onClick={() => startEdit(c)} variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10 text-white text-sm" data-testid={`edit-car-${c.id}`}><Pencil size={14} className="mr-1.5" /> Edit</Button>
                      <Button onClick={() => toggleSold(c.id)} variant="outline" className={`text-sm ${c.is_sold ? "border-[#25D366]/40 bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#25D366]" : "border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400"}`} data-testid={`sold-toggle-${c.id}`}>
                        {c.is_sold ? <><RotateCcw size={14} className="mr-1.5" /> Available</> : <><Tag size={14} className="mr-1.5" /> Sold</>}
                      </Button>
                    </div>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" className="mt-2 w-full border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm" data-testid={`delete-car-${c.id}`}><Trash2 size={14} className="mr-1.5" /> Delete Permanently</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#141414] border-white/10 text-white">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove this car?</AlertDialogTitle>
                          <AlertDialogDescription className="text-gray-400">{c.brand} {c.model} ({c.year}) will be removed from your inventory. Use "Sold" instead if you want to keep showing it with a Sold badge.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteCar(c.id)} className="bg-[#FF3B30] hover:bg-[#FF5247]" data-testid="confirm-delete">Yes, Remove</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ENQUIRIES */}
        <TabsContent value="enquiries" className="mt-8">
          {enquiries.length === 0 ? (
            <div className="border border-dashed border-white/15 rounded-xl py-20 text-center text-gray-400 flex flex-col items-center gap-3"><Inbox size={32} /> No loan enquiries yet.</div>
          ) : (
            <div className="space-y-4">
              {enquiries.map((en) => (
                <div key={en.id} className="bg-[#141414] border border-white/10 rounded-lg p-5 flex flex-wrap gap-x-8 gap-y-2" data-testid={`enquiry-${en.id}`}>
                  <div><p className="text-xs uppercase text-gray-500 font-bold">Name</p><p className="text-white font-semibold">{en.name}</p></div>
                  <div><p className="text-xs uppercase text-gray-500 font-bold">Phone</p><a href={`tel:${en.phone}`} className="text-[#FF3B30] font-semibold">{en.phone}</a></div>
                  <div><p className="text-xs uppercase text-gray-500 font-bold">Car</p><p className="text-white">{en.car_interested || "-"}</p></div>
                  {en.message && <div className="w-full"><p className="text-xs uppercase text-gray-500 font-bold">Message</p><p className="text-gray-300 text-sm">{en.message}</p></div>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboard;
